import React, { useRef, useState } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  Image,
  StyleSheet,
  Platform,
} from "react-native";
import { AppTheme } from "../utils/theme";
import { AppIcons } from "../assets/images";
import LinearGradient from "react-native-linear-gradient";
import ViewShot from "react-native-view-shot";
import Spacer from "./global/Spacer";
import Share from "react-native-share";

type GameOverModalProps = {
  visible: boolean;
  winners: string[]; // Assuming array of player identifiers (colors for online, names for offline)
  onRestart: () => void;
  onClose: () => void;
  isOnline?: boolean;
  onlinePlayers?: any[]; // Assuming each has { color: string, name: string, image?: string | number } for avatar
  userAvatar?: string | number; // Optional: for "You" avatar in offline/single player
};

const GameOverModal: React.FC<GameOverModalProps> = ({
  visible,
  winners,
  onRestart,
  onClose,
  isOnline,
  onlinePlayers,
  userAvatar,
}) => {
  const modalRef = useRef<any>(null);
  const [showBtn, setShowBtn] = useState<boolean>(true);

  const onSharePress = async () => {
    try {
      // Capture image properly
      const imgUrl = await modalRef?.current?.capture();

      const appStoreLink =
        Platform.OS === "ios"
          ? "https://apps.apple.com/in/app/ludo-voice/id1511172928"
          : "https://play.google.com/store/apps/details?id=com.netscapelabs.ludovoice";

      const message = `🎲 Just played an amazing match on *Ludo Voice* and won! 😎

Voice chat + Ludo = next level fun 🔥  
Join me and let’s play together!

📲 Download her

👇 Download the app now:
${appStoreLink}`;

      await Share.open({
        title: "I just won on Ludo Voice! 🏆",
        message:message,
        url: imgUrl, // captured image path
        type: "image/png",
      });
      setShowBtn(true);
    } catch (err) {
      setShowBtn(true);
      console.log("Share error", err);
    }
  };

  return (
    <Modal animationType="fade" transparent visible={visible}>
      <View style={styles.overlay}>
        <ViewShot
          ref={modalRef}
          options={{
            fileName: Date.now()?.toString(),
            format: "png",
          }}
          style={{paddingTop:200}}
        >
          <View style={styles.box}>
            {/* Background Crown */}
            <View style={styles.bigCrownWrapper}>
              <Image
                source={AppIcons.Crown}
                style={styles.crown}
                resizeMode="contain"
              />
            </View>

            {/* Winners Leaderboard */}
            <View style={styles.winnerContainer}>
              {winners.slice(0, 4).map((item, index) => {
                let playerName = item;
                let playerAvatar: string | number | undefined;
                const rank = index + 1; // 1, 2, 3, 4

                if (isOnline) {
                  const findPlayer = onlinePlayers?.find(
                    (e) => e?.color === item
                  );
                  playerName = findPlayer?.name || item;
                  playerAvatar = findPlayer?.image;
                } else if (index === 0) {
                  // playerName = "You";
                  playerAvatar = userAvatar;
                }

                return (
                  <View
                    key={index}
                    style={[
                      styles.winnerCard,
                      index === 0 ? styles.firstWinner : styles.otherWinner,
                    ]}
                  >
                    <View
                      style={[
                        styles.row,
                        {
                          backgroundColor:
                            index === 0
                              ? AppTheme.colors.winnerButton
                              : AppTheme.colors.otherButton,
                        },
                      ]}
                    >
                      <View style={styles.rankContainer}>
                        <Image
                          source={
                            index === 0 ? AppIcons.Count : AppIcons.CountOther
                          }
                          style={[styles.rankImage]}
                          resizeMode="contain"
                        />
                        <Text style={styles.rankText}>{rank}</Text>
                      </View>
                    </View>
                    <Text
                      style={[
                        styles.winnerText,
                        index === 0
                          ? styles.firstWinnerText
                          : styles.otherWinnerText,
                      ]}
                    >
                      {playerName}
                    </Text>
                  </View>
                );
              })}
            </View>
            {showBtn && (
              <>
                <Spacer size={14} />
                <View style={styles.btnView}>
                  <TouchableOpacity onPress={onRestart}>
                    <Image
                      source={AppIcons.PlayAgainBtn}
                      style={styles.playBtn}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                  <Spacer row size={20} />
                  <TouchableOpacity
                    onPress={() => {
                      setShowBtn(false);
                      setTimeout(() => {
                        onSharePress();
                      }, 2000);
                    }}
                  >
                    <Image
                      source={AppIcons.ShareBtn}
                      style={styles.shareBtn}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                </View>
              </>
            )}
          </View>
        </ViewShot>

        {/* Play Again */}
        {/* <TouchableOpacity
          onPress={onRestart}
          activeOpacity={1}
          style={styles.googleWrapper}
        >
          <LinearGradient
            colors={[
              AppTheme.colors.linearGradient_5,
              AppTheme.colors.linearGradient_6,
            ]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.googleBtn1}
          >
            <LinearGradient
              colors={[
                AppTheme.colors.linearGradient_1,
                AppTheme.colors.linearGradient_2,
              ]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.googleBtn}
            >
              <Text style={styles.googleText}>PLAY AGAIN</Text>
            </LinearGradient>
          </LinearGradient>
        </TouchableOpacity> */}
        <TouchableOpacity
          style={styles.backButton}
          activeOpacity={0.7}
          onPress={onClose}
        >
          <Image
            source={AppIcons.BackIcon}
            resizeMode="contain"
            style={styles.backIcon}
          />
        </TouchableOpacity>
      </View>
    </Modal>
  );
};

export default GameOverModal;

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.6)",
    alignItems: "center",
  },

  box: {
    width: "90%",
    borderRadius: 50,
    alignItems: "center",
    padding: 15,
    zIndex: 0,
    backgroundColor: AppTheme.colors.winnerModalBack,
    borderWidth: 5,
    borderColor: AppTheme.colors.winnerModalBorder,
  },

  bigCrownWrapper: {
    zIndex: -1,
    alignItems: "center",
    marginLeft: 5,
  },

  crown: {
    width: 310,
    height: 260,
    position: "absolute",
    zIndex: -5,
    top: -210,
  },
  youWinText: {
    fontSize: 20,
    color: AppTheme.colors.white,
    fontWeight: "bold",
  },

  winnerContainer: {
    alignItems: "center",
    width: "100%",
    borderRadius: 10,
    paddingVertical: 5,
    marginTop: 20,
  },

  winnerCard: {
    width: "100%",
    height: 60,
    borderRadius: 10,
    marginVertical: 3,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    // shadowColor: AppTheme.colors.black,
    // shadowOffset: {
    //   width: 0,
    //   height: 0,
    // },
    // shadowOpacity: 0.3,
    // shadowRadius: 25,
    // elevation: 5,
  },
  backButton: {
    position: "absolute",
    top: Platform.OS === "android" ? 35 : 70,
    right: 0,
    width: 50,
    height: 40,
    zIndex: 10,
  },
  backIcon: {
    width: 50,
    height: 40,
  },
  firstWinner: {
    backgroundColor: AppTheme.colors.winnerBorder, // Gold/yellow for 1st
    borderWidth: 3,
    borderColor: AppTheme.colors.linearGradient_5,
  },
  row: {
    height: 54.5,
    width: 56,
    marginLeft: -12.5,
    justifyContent: "center",
    alignItems: "center",
    borderTopLeftRadius: 5,
    borderBottomLeftRadius: 5,
    borderRightWidth: 2,
    borderColor: "rgba(0,0,0,0.1)",
  },
  otherWinner: {
    backgroundColor: AppTheme.colors.winnerBack, // Purple for others
    borderWidth: 3,
    borderColor: AppTheme.colors.linearGradient_3,
  },

  avatarContainer: {
    width: 30,
    height: 30,
    borderRadius: 15,
    overflow: "hidden",
    marginRight: 12,
  },

  avatar: {
    width: "100%",
    height: "100%",
  },

  rankContainer: {
    width: 30,
    height: 30,
    position: "relative",
    alignItems: "center",
    justifyContent: "center",
  },

  rankImage: {
    width: 40,
    height: 40,
    position: "absolute",
  },

  rankText: {
    fontSize: 20,
    color: AppTheme.colors.darkBrown,
    position: "absolute",
    fontFamily: AppTheme.fonts.ALEXANDRIA_BOLD,
    zIndex: 1,
    textAlign: "center",
  },

  winnerText: {
    fontSize: 16,
    fontWeight: "700",
    flex: 1,
    marginLeft: 20,
  },

  firstWinnerText: {
    color: "#000", // Black for contrast on gold
  },

  otherWinnerText: {
    color: AppTheme.colors.white, // White for purple
  },
  googleWrapper: {
    alignSelf: "center",
    marginTop: 10,
  },
  googleBtn: {
    width: 250,
    height: 50,
    borderRadius: 25,
    justifyContent: "center",
  },

  googleBtn1: {
    width: 250,
    height: 55,
    borderRadius: 25,
  },
  googleText: {
    fontFamily: AppTheme.fonts.BUBBLE_BOBBLE,
    fontSize: 35,
    color: AppTheme.colors.white,
    textAlign: "center",
    textShadowColor: AppTheme.colors.outline,
    textShadowOffset: { width: 2, height: 2 },
    textShadowRadius: 2,
  },

  playAgainBtn: {
    backgroundColor: AppTheme.colors.skycolor,
    paddingVertical: 12,
    width: 230,
    borderRadius: 10,
    marginTop: 18,
    alignItems: "center",
  },

  playAgainText: {
    fontSize: 24,
    color: AppTheme.colors.white,
    fontWeight: "bold",
    borderWidth: 0.5,
    borderColor: AppTheme.colors.white,
    paddingHorizontal: 30,
    borderRadius: 10,
    paddingVertical: 4,
  },
  playBtn: {
    width: 180,
    height: 60,
  },
  shareBtn: {
    width: 120,
    height: 60,
  },
  btnView: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
  },
});
